import { CollectionConfig } from "@rebasepro/types";
// Type-only, so nothing from the admin bundle reaches the backend that loads
// this file for its schema.
import type { EntityAction } from "@rebasepro/cms-types";
import { IS_REAL_USER } from "./_auth_predicates";
import { deriveJobCanon } from "../taxonomy/derive";
import { JOB_FUNCTIONS } from "../taxonomy/job-functions";
import { publicJobUrl } from "../public_site";
import companiesCollection from "./companies";

/**
 * "Show me this the way a candidate sees it."
 *
 * The panel could describe a vacancy in sixty-six fields and still not answer
 * whether the board renders it properly — whether the logo resolved, whether the
 * markdown sections came out as prose, whether the salary block is there at all.
 * That question only has one answer and it is on the public site, which is a
 * different origin and was reachable from here only by assembling the URL by
 * hand from the row's id.
 *
 * A plain object rather than a React component on purpose: this file is loaded
 * by the backend for its schema, and an action that only opens a window needs
 * nothing from the UI layer. The icon is a name for the same reason.
 *
 * `noopener` because the target is a window we hand an origin we control but do
 * not want holding a reference back to the panel's `window`.
 */
const viewOnPublicBoard: EntityAction = {
    key: "view_on_public_board",
    name: "Ver en el tablón",
    icon: "ExternalLink",
    // Every other status is either not yet public (`pending_review`) or no
    // longer public (`closed`), and the board answers both with "no encontrada"
    // — a link that reliably 404s is worse than a disabled one, because the
    // reader blames the vacancy rather than the link.
    isEnabled: ({ entity }) => entity?.values?.status === "published",
    onClick: ({ entity }) => {
        if (!entity?.id) return;
        window.open(publicJobUrl(String(entity.id)), "_blank", "noopener,noreferrer");
    },
    // Worth a click from the list as well as from the form: checking a batch of
    // freshly published vacancies means opening each one otherwise.
    showActionsInListView: true
};

/**
 * The two sources that mean "ours": a company filled the form on the site, or
 * the team loaded the vacancy by hand. Everything else arrived from somebody
 * else's job board.
 */
const OUR_SOURCES = new Set(["submission", "manual"]);

/**
 * `is_imported`, derived from `source` — the column the board and this panel
 * order by.
 *
 * The fact is already in `source`, and that is exactly the problem: `orderBy`
 * compiles to `ORDER BY <column> <direction>` and takes no expression, so
 * "ours first" cannot be asked for while the answer is spread across five enum
 * values declared in an order that interleaves them. So it is denormalised onto
 * the row, the way drizzle/0058 explains, and kept true here rather than at each
 * caller — the same arrangement as the `_canon` columns above.
 *
 * Stated as "not ours" rather than as a list of the three importers on purpose.
 * A scraper added next year gets a new `jobs_source` value, and the safe default
 * for an unrecognised one is that we brought it in from elsewhere: a vacancy
 * should never reach the top of the board because nobody remembered to extend a
 * list. A row with no `source` at all is ours — the importers always stamp one,
 * so an empty source is a record somebody typed into the panel.
 *
 * Absent on both sides means absent from the write, the rule `deriveJobCanon`
 * follows: a cron touching `view_count` says nothing about where the vacancy
 * came from, and must not be read as saying it came from nowhere.
 */
export function deriveJobProvenance(
    values: Record<string, unknown>,
    previousValues?: Record<string, unknown>
): Record<string, unknown> {
    const source = "source" in values ? values.source : previousValues?.source;
    if (source === undefined) return {};
    return { is_imported: typeof source === "string" && !OUR_SOURCES.has(source) };
}

/**
 * Who may write the note that goes back to a company: the team, or the backend
 * itself (a cron or script has no request user). A company member may not.
 *
 * RLS already refuses a company that tries to *return* its own vacancy (the
 * `company_update_own_unpublished_jobs` check, drizzle/0066). This covers the
 * note on its own: a company that rewrote the text the team sent it would
 * change what the panel says we asked for.
 */
function writesForTheTeam(user: { roles?: string[] } | null | undefined): boolean {
    return !user || (user.roles ?? []).includes("admin");
}

/**
 * What a company's write may not change, pinned to what the row already holds.
 *
 * Overrides rather than deletions, and that is load-bearing: Rebase merges a
 * `beforeSave` result *into* the payload (`mergeDeep(values, result)`), so a
 * key left out of the result survives exactly as the caller sent it. The only
 * way to undo a field is to return it with the value it should have — the
 * row's own, or nothing on a new row. Empty when the writer is the team or the
 * payload does not touch either column.
 */
export function pinTeamOnlyFields(
    values: Record<string, unknown>,
    previousValues: Record<string, unknown> | undefined,
    user: { roles?: string[] } | null | undefined
): Record<string, unknown> {
    if (writesForTheTeam(user)) return {};
    const pinned: Record<string, unknown> = {};
    if ("return_note" in values) pinned.return_note = previousValues?.return_note ?? null;
    if ("returned_at" in values) pinned.returned_at = previousValues?.returned_at ?? null;
    return pinned;
}

/**
 * The dates the database keeps, held at the row's own value on every update.
 *
 * `published_at` is stamped by the `jobs_stamp_published_at` trigger (0068) and
 * `closing_reminder_sent_at` by the closing-reminder cron, both in SQL that
 * never passes through here. But Rebase's entity form sends every value back on
 * save, read-only ones included, and a form opened before either write still
 * holds the old value. So "Publicar" and then fixing a typo in the same form put
 * `published_at` back to NULL — the board figures and the traffic count lost the
 * day the vacancy went live — and a form left open across the 13:00 cron
 * cleared the reminder stamp, and the poster got a second reminder.
 *
 * Nobody writes either through the API, the team included, so the pin is for
 * everyone and only on update: a create has no row to be stale against.
 * `returned_at` is the same story while the vacancy stays returned; on the
 * transition into `returned`, `deriveReturnState` stamps it after this runs.
 */
export function pinDatabaseStamps(
    values: Record<string, unknown>,
    previousValues: Record<string, unknown> | undefined
): Record<string, unknown> {
    if (!previousValues) return {};
    const pinned: Record<string, unknown> = {};
    for (const key of ["published_at", "closing_reminder_sent_at"]) {
        if (key in values) pinned[key] = previousValues[key] ?? null;
    }
    const staysReturned = previousValues.status === "returned"
        && (!("status" in values) || values.status === "returned");
    if (staysReturned && "returned_at" in values) pinned.returned_at = previousValues.returned_at ?? null;
    return pinned;
}

/** Thrown, and shown in the panel, when a vacancy is sent back with nothing to say. */
export const RETURN_NEEDS_A_NOTE =
    "Escribe la nota para la empresa antes de devolver la vacante: es lo que recibirá por correo.";

/**
 * `return_note` and `returned_at`, kept true to the status they describe.
 *
 * Two rules, and the first is the one that matters:
 *
 *  - **A published vacancy carries no note.** `public_read_published` exposes
 *    every column of a published row to anyone, so a note written to one
 *    company about its posting would go public the moment the posting did.
 *    Both columns are cleared on the write that publishes. The database refuses
 *    the row otherwise (`jobs_return_note_is_never_public`), so a publish that
 *    skipped this would fail rather than leak.
 *  - **A vacancy is not sent back empty-handed.** The note is the whole email;
 *    without it the company is told "we sent it back" and not why. Refused here
 *    with a sentence the panel can show, before the CHECK refuses it with one
 *    it cannot.
 *
 * `returned_at` is stamped on the transition into `returned` only, so a later
 * edit to the note does not move the date the company was written to.
 *
 * Absent means absent, as in `deriveJobCanon`: a cron bumping `view_count` on
 * a returned vacancy says nothing about its note.
 */
export function deriveReturnState(
    values: Record<string, unknown>,
    previousValues?: Record<string, unknown>,
    now: Date = new Date()
): Record<string, unknown> {
    const nextStatus = "status" in values ? values.status : previousValues?.status;

    if (nextStatus === "published") {
        const carriesOne = [values.return_note, values.returned_at, previousValues?.return_note, previousValues?.returned_at]
            .some(value => value !== undefined && value !== null && value !== "");
        return carriesOne ? { return_note: null, returned_at: null } : {};
    }

    if (nextStatus !== "returned") return {};

    const raw = "return_note" in values ? values.return_note : previousValues?.return_note;
    const note = typeof raw === "string" ? raw.trim() : "";
    if (!note) throw new Error(RETURN_NEEDS_A_NOTE);

    const out: Record<string, unknown> = {};
    if ("return_note" in values) out.return_note = note;
    if (previousValues?.status !== "returned") out.returned_at = now.toISOString();
    return out;
}

const jobsCollection: CollectionConfig = {
    name: "Vacantes",
    singularName: "Vacante",
    slug: "jobs",
    table: "jobs",

    securityRules: [
        {
            name: "public_read_published",
            operation: "select",
            using: "status = 'published'"
        },
        {
            // `auth.uid() IS NOT NULL` alone is a tautology on the user path:
            // anonymous visitors carry a sentinel, not NULL. The sentinel is
            // 'anon', not 'anonymous' — excluding only the latter left this
            // insert open to signed-out callers. See IS_REAL_USER.
            name: "company_insert_pending",
            operation: "insert",
            using: `${IS_REAL_USER} AND status = 'pending_review'`,
            withCheck: `${IS_REAL_USER} AND status = 'pending_review'`
        },
        {
            // Without this a company cannot post at all: the API inserts with
            // RETURNING, Postgres applies SELECT policies to the returned row,
            // and a fresh posting is 'pending_review' — which
            // `public_read_published` does not match. The INSERT succeeds and
            // the statement still fails with 42501.
            name: "company_read_own_jobs",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = jobs.company_id)"
        },
        {
            // Edit a vacancy that is not yet live. The company owns its copy
            // right up to the moment an admin publishes it.
            //
            // Both clauses matter and they check different rows: `using` sees
            // the row as it is (must not already be published), `withCheck`
            // sees the row as it would become (must not become published).
            // Together they let a company fix a typo on a pending posting while
            // leaving `status = 'published'` unreachable — the moderation gate
            // `company_insert_pending` establishes on the way in.
            //
            // `returned` is refused as a *new* status for the same reason
            // `published` is: it is the team's answer, not the company's. A
            // return emails `poster_email` the note, and a company can write
            // both columns — so without this clause a company could have us
            // send any text to any address. The `using` half still admits a
            // returned row, which is how the company fixes one: the dashboard
            // moves it to `pending_review` in the same write (drizzle/0066).
            name: "company_update_own_unpublished_jobs",
            operation: "update",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = jobs.company_id) AND status <> 'published'",
            withCheck: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = jobs.company_id) AND status <> 'published' AND status <> 'returned'"
        },
        {
            // Retire a live vacancy: published → closed (the dashboard's
            // "Cerrar"), or published → pending_review to pull it back for
            // edits. Without this a company could not take its own posting
            // down at all — Cerrar, inline edit and delete each failed, because
            // no policy admitted an UPDATE or DELETE by a company member.
            //
            // Kept as its own rule rather than folded into the one above:
            // permissive policies are OR-ed, so a single rule covering both the
            // published and unpublished cases would also admit
            // pending_review → published, which is the one transition that has
            // to stay an admin's.
            name: "company_retire_own_published_jobs",
            operation: "update",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = jobs.company_id) AND status = 'published'",
            withCheck: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = jobs.company_id) AND status IN ('closed', 'pending_review')"
        },
        {
            // Delete your own vacancy while it is not live — a live one is
            // closed. That is all this rule says, and on its own it is not
            // enough: see the restrictive rule below, which is what keeps a
            // vacancy's candidaturas.
            name: "company_delete_own_unpublished_jobs",
            operation: "delete",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = jobs.company_id) AND status <> 'published'"
        },
        {
            // `company_insert_pending` lets any signed-in account file a
            // vacancy; this says for whom. No company, or one you are a member
            // of — the vacancy form sends nothing else. Without it a candidate
            // could put a vacancy of their own wording into any company's
            // dashboard. Restrictive so an old config's boot cannot widen it.
            // drizzle/0075.
            name: "vacancies_filed_only_for_own_company",
            operation: "insert",
            mode: "restrictive",
            using: "auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin'] OR company_id IS NULL OR public.is_company_member(company_id)",
            withCheck: "auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin'] OR company_id IS NULL OR public.is_company_member(company_id)"
        },
        {
            // `returned` is the team's word, and it sends the return note to
            // poster_email from hola@. 0066 kept it out of the company's
            // update policy above; a pre-0066 backend re-creates that policy
            // without the exclusion, so it is held here too. drizzle/0075.
            name: "only_the_team_returns_vacancies",
            operation: "update",
            mode: "restrictive",
            using: "true",
            withCheck: "auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin'] OR status IS DISTINCT FROM 'returned'"
        },
        {
            // …and never one somebody applied to. `talent_applications.job_id`
            // is ON DELETE CASCADE, so deleting such a vacancy deletes its
            // candidaturas — for the candidate, the employer and the team.
            // "Not published" did not prevent that: a vacancy published and
            // then closed, or pulled back to review, passed, and a company can
            // move its own closed vacancy back to review anyway, so no status
            // test can hold. A vacancy with candidaturas is closed instead.
            //
            // The count goes through `public.job_has_applications`, a SECURITY
            // DEFINER function, and must not be inlined into a plain
            // `NOT EXISTS (SELECT … FROM talent_applications …)`. A company
            // sees applications by `talent_applications.company_id`, which the
            // applicant writes and nothing ties to the vacancy's company, so
            // that subquery would miss exactly the rows the cascade then
            // deletes unseen.
            //
            // Restrictive, and a rule of its own rather than a clause above:
            // Rebase re-creates declared policies on every boot, so an instance
            // on an older revision puts the old delete rule back. Restrictive
            // is ANDed with every permissive rule and no old config declares
            // it, so it holds whichever revision booted last. Admins and the
            // trusted server context pass. drizzle/0073.
            name: "only_admins_delete_applied_jobs",
            operation: "delete",
            mode: "restrictive",
            using: "auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin'] OR NOT public.job_has_applications(jobs.id)"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    history: true,

    /**
     * Derive the `_canon` columns from the open lists, and `is_imported` from
     * `source`, on every write.
     *
     * Here rather than at each caller because there are four of them — the
     * vacancy form on the site, the admin panel, the scraper's LLM cleaner and
     * the enrichment cron — and `beforeSave` is the one point all four pass
     * through. See `taxonomy/derive.ts` and `deriveJobProvenance` above.
     *
     * `previousValues` matters only for the second one: an update that does not
     * carry `source` still has to land on the right side of the board, and the
     * row already knows which side that is.
     */
    callbacks: {
        beforeSave: ({ values, previousValues, context }) => {
            const previous = previousValues as Record<string, unknown> | undefined;
            const own = {
                ...(values as Record<string, unknown>),
                ...pinTeamOnlyFields(values as Record<string, unknown>, previous, context?.user),
                ...pinDatabaseStamps(values as Record<string, unknown>, previous)
            };
            return {
                ...own,
                ...deriveJobCanon(own),
                ...deriveJobProvenance(own, previousValues),
                ...deriveReturnState(own, previous)
            };
        }
    },

    // ── Form field ordering (most important first) ──────────────
    // The public job board's search (`src/components/react/JobBoard.tsx`) runs
    // through this. Without the block it was `ILIKE '%term%'` over top-level
    // string columns, which meant a candidate searching "gestion ambiental"
    // matched only if that exact substring sat in one column — never across the
    // description, never in `skills` or `tags`, and never with an accent typed
    // differently from how the employer wrote it.
    //
    // Weights follow how a job seeker reads a posting: the title and the
    // company first, then what the role actually asks for, then the prose.
    search: {
        language: "spanish",
        unaccent: true,
        fuzzy: true,
        fields: [
            { path: "title", weight: "A" },
            { path: "skills", weight: "A" },
            // The open credential lists, at the same weight as `skills` and for
            // the same reason: they say what the role asks for. They matter
            // here more than `skills` does, because 22% of what people write in
            // these fields normalizes to no canonical id — "Certified Expert in
            // Green Finance", "FMVA (CFI)" — and full text is the only way that
            // 22% is reachable at all.
            //
            // The raw columns, deliberately, not the `_canon` ones: canon holds
            // identifiers like `iso_14001`, and indexing those would match a
            // search for "iso" against every ISO standard at once.
            { path: "required_certifications", weight: "A" },
            { path: "required_skills", weight: "A" },
            { path: "desired_certifications", weight: "B" },
            { path: "desired_skills", weight: "B" },
            { path: "tags", weight: "B" },
            { path: "sustainability_focus", weight: "B" },
            { path: "company_name", weight: "B" },
            { path: "department", weight: "B" },
            { path: "industry", weight: "B" },
            { path: "summary", weight: "C" },
            { path: "requirements_md", weight: "C" },
            { path: "responsibilities_md", weight: "C" },
            { path: "location_display", weight: "C" },
            { path: "location_city", weight: "C" },
            // The long prose last: a passing mention in the company blurb must
            // not outrank a job whose title is the thing being searched for.
            { path: "description_md", weight: "D" },
            { path: "about_company_md", weight: "D" }
        ]
        // Not indexed: the enums (`work_model`, `seniority`, `employment_type`,
        // `status`, `location_continent`) — a fixed vocabulary the board
        // already filters on, and the block refuses them anyway.
    },
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },

        // ── Identity ─────────────────────────────────────────────
        title: {
            name: "Título",
            type: "string",
            validation: { required: true }
        },
        // In the public vacancy URL. Retyping it orphans every link already
        // shared for this job.
        slug: {
            name: "Slug",
            type: "string",
            description: "Identificador para URLs (lo genera el LLM)",
            admin: { readOnly: true }
        },
        // These two were both called "Empresa", which is why nobody could say
        // what either was for. They answer different questions and disagree on
        // 44 of 107 vacancies — "PNUD México" against the UNDP record, "Banco
        // BCI" against Bci, a named ministry against "Gobierno de México".
        //
        // The text is what this posting *says*; it is per-vacancy, it is what
        // the public board prints, and it is the only one that can say
        // "Confidencial". The relation is who the employer *is*: the record that
        // owns the logo, the brand and every other vacancy from the same
        // organisation. It is resolved by `job-company-linker`, which is to say
        // inferred — so overwriting the text with it would replace something
        // stated with something guessed.
        company_name: {
            name: "Nombre en la oferta",
            description: "Lo que esta vacante llama a la empresa. Es lo que se publica — puede ser 'Confidencial'.",
            type: "string",
            validation: { required: true }
        },
        company_id: {
            name: "Ficha de empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            description: "A qué empresa pertenece la vacante. De aquí salen el logo y la marca; puede diferir del nombre de arriba."
        },

        status: {
            name: "Estado",
            type: "string",
            enum: [
                { id: "pending_review", label: "Pendiente de revisión", color: "orange" },
                { id: "published", label: "Publicado", color: "green" },
                // Sent back to the company with a note. Not a rejection: the
                // company fixes it from its dashboard and it returns here as
                // "Pendiente de revisión". See drizzle/0066.
                { id: "returned", label: "Devuelta con nota", color: "purple" },
                { id: "draft", label: "Borrador", color: "gray" },
                { id: "closed", label: "Cerrado", color: "red" }
            ],
            description: "Para una vacante que envió una empresa, publícala o devuélvela desde «Resumen»: así la empresa recibe el correo que le corresponde.",
            defaultValue: "draft"
        },

        return_note: {
            name: "Nota para la empresa",
            type: "string",
            description: "Lo que le pedimos que cambie al devolverla. Se le envía por correo y la ve en su panel. Se borra al publicar: una vacante publicada es pública entera.",
            admin: { multiline: true }
        },
        returned_at: {
            name: "Devuelta el",
            type: "date",
            description: "Cuándo se le devolvió por última vez. Se escribe sola.",
            admin: { readOnly: true }
        },


        // ── Location (structured) ────────────────────────────────
        location_display: {
            name: "Ubicación",
            type: "string",
            description: "Texto para mostrar: 'Madrid, España'"
        },
        location_city: {
            name: "Ciudad",
            type: "string"
        },
        location_state: {
            name: "Estado / Región",
            type: "string"
        },
        location_country: {
            name: "País",
            type: "string",
            description: "Nombre completo del país en español"
        },
        location_country_code: {
            name: "Código País",
            type: "string",
            description: "ISO 3166-1 alpha-2 (ES, MX, CO…)"
        },
        location_continent: {
            name: "Continente",
            type: "string",
            enum: [
                { id: "latinoamerica", label: "Latinoamérica", color: "green" },
                { id: "europa", label: "Europa", color: "blue" },
                { id: "norteamerica", label: "Norteamérica", color: "purple" },
                { id: "asia", label: "Asia", color: "orange" },
                { id: "africa", label: "África", color: "yellow" },
                { id: "oceania", label: "Oceanía", color: "teal" },
                { id: "global", label: "Global / Remoto", color: "gray" }
            ]
        },
        // Geocoded from the location fields above by the enrichment job. Edit
        // the human-readable location and let the pipeline re-derive these;
        // typing coordinates directly puts the pin somewhere the text doesn't say.
        location_lat: {
            name: "Latitud",
            type: "number",
            admin: { readOnly: true }
        },
        location_lng: {
            name: "Longitud",
            type: "number",
            admin: { readOnly: true }
        },

        // ── Work model ───────────────────────────────────────────
        work_model: {
            name: "Modalidad",
            type: "string",
            enum: [
                { id: "on_site", label: "Presencial", color: "orange" },
                { id: "remote", label: "Remoto", color: "blue" },
                { id: "hybrid", label: "Híbrido", color: "teal" }
            ]
        },
        remote_regions: {
            name: "Regiones Remotas",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Región" },
            description: "Regiones elegibles para trabajo remoto"
        },

        // ── Employment ───────────────────────────────────────────
        /**
         * Hours. Not the contract.
         *
         * This enum used to carry both: full_time and part_time answer how many
         * hours, while internship, contract, freelance and temporary answer what
         * the working relationship is — and they are not alternatives, since a
         * contractor usually works full time. Asking for one value forced a
         * posting to drop whichever fact it happened to state second.
         *
         * The four relationship values moved to `contract_type` in drizzle/0026
         * and are no longer offered here. They remain in the Postgres enum only
         * because a value cannot be dropped from one; nothing writes them.
         *
         * Empty means the posting did not say, which is a fact about the posting
         * and not a synonym for full time — the scraper used to return full_time
         * as a fallback, and 89 of 107 live jobs inherited it.
         */
        employment_type: {
            name: "Jornada",
            type: "string",
            enum: [
                { id: "full_time", label: "Tiempo completo", color: "blue" },
                { id: "part_time", label: "Medio tiempo", color: "teal" }
            ],
            description: "Cuántas horas. Vacío = la oferta no lo dice."
        },
        contract_type: {
            name: "Tipo de contrato",
            type: "string",
            enum: [
                { id: "permanent", label: "Indefinido", color: "green" },
                { id: "fixed_term", label: "Temporal (con fecha)", color: "orange" },
                { id: "freelance", label: "Freelance", color: "yellow" },
                { id: "internship", label: "Prácticas", color: "purple" },
                { id: "temporary", label: "Eventual", color: "gray" }
            ],
            description: "Qué relación laboral. Independiente de la jornada."
        },
        seniority: {
            name: "Nivel",
            type: "string",
            enum: [
                { id: "intern", label: "Pasante", color: "purple" },
                { id: "junior", label: "Junior", color: "teal" },
                { id: "mid", label: "Intermedio", color: "blue" },
                { id: "senior", label: "Senior", color: "green" },
                { id: "lead", label: "Responsable de equipo", color: "orange" },
                { id: "manager", label: "Gerente", color: "yellow" },
                { id: "director", label: "Director", color: "red" },
                { id: "executive", label: "Alta dirección", color: "gray" }
            ]
        },
        department: {
            name: "Departamento",
            type: "string",
            description: "Área funcional: Sostenibilidad, Ingeniería, Operaciones…"
        },
        /**
         * What kind of work this is — one value, from a closed list.
         *
         * `department` above is whatever the posting called the team, and 27
         * of 75 published vacancies call it "Sostenibilidad". This is what the
         * weekly digest filters on, so a candidate who asked for engineering
         * does not get the inside-sales role. One per vacancy on purpose: the
         * area filter carried several per vacancy and let almost everything
         * through. See config/taxonomy/job-functions.ts and drizzle/0076.
         *
         * Filled by the model on the way in; this is where it gets corrected.
         * No `defaultValue` — a config default beats the column's NULL on
         * every SDK create, and "not classified yet" must stay representable.
         */
        job_function: {
            name: "Tipo de trabajo",
            type: "string",
            enum: JOB_FUNCTIONS.map(({ id, label }) => ({ id, label })),
            description: "Lo que haría la persona contratada la mayor parte del tiempo. Uno solo. Lo filtra el boletín de vacantes."
        },
        experience_years: {
            name: "Años de experiencia requeridos",
            type: "string",
            description: "Años de experiencia requeridos para la vacante"
        },
        required_studies: {
            name: "Estudios específicos solicitados",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Estudio" },
            description: "Estudios específicos solicitados (múltiples entradas)"
        },
        required_languages: {
            name: "Idiomas y nivel",
            type: "array",
            columnType: "jsonb",
            of: { type: "map", name: "Idioma" },
            description: "Idiomas y nivel requeridos (formato estructurado: idioma + nivel)"
        },
        travel_required: {
            name: "Requiere viajar",
            type: "boolean",
            description: "Esta posición requiere que viaje",
            defaultValue: false
        },

        // ── Certifications and skills ────────────────────────────
        /**
         * Four open lists and the four derived ones beside them. See
         * `taxonomy/credentials.ts` for why each field is stored twice, and
         * drizzle/0037 for the columns.
         *
         * Open on purpose: the closed `skills` enum below could not express
         * "ISO 14046" or "PCAF", and a vacancy that needs one of those should
         * say so rather than pick the nearest of 25 approximations. The
         * `_canon` siblings are what makes the open text queryable again.
         *
         * Required and desired are separate fields rather than one list with a
         * flag because that is how they are read: the required set gates a
         * candidate, the desired set scores them. `ApplicantsPanel` does both.
         */
        required_certifications: {
            name: "Certificaciones obligatorias",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Certificación" },
            description: "Sin estas, la candidatura no cumple. Escribe cualquier cosa; se sugieren las conocidas.",
            admin: { span: 4, Field: "../fields/CredentialsField" }
        },
        desired_certifications: {
            name: "Certificaciones deseables",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Certificación" },
            description: "Suman, pero no descartan a quien no las tenga.",
            admin: { span: 4, Field: "../fields/CredentialsField" }
        },
        required_skills: {
            name: "Habilidades obligatorias",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Habilidad" },
            description: "Sin estas, la candidatura no cumple.",
            admin: { span: 4, Field: "../fields/CredentialsField" }
        },
        desired_skills: {
            name: "Habilidades deseables",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Habilidad" },
            description: "Suman, pero no descartan.",
            admin: { span: 4, Field: "../fields/CredentialsField" }
        },

        /**
         * Derived, never typed. `beforeSave` rewrites all four from the lists
         * above on every save, so anything entered here is overwritten on the
         * next one — hidden rather than merely read-only for that reason.
         *
         * They keep their columns and their GIN indexes: these are what every
         * candidate-to-vacancy query actually matches on.
         */
        required_certifications_canon: {
            name: "Certificaciones obligatorias (normalizadas)",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Id" },
            conditions: { hidden: true }
        },
        desired_certifications_canon: {
            name: "Certificaciones deseables (normalizadas)",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Id" },
            conditions: { hidden: true }
        },
        required_skills_canon: {
            name: "Habilidades obligatorias (normalizadas)",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Id" },
            conditions: { hidden: true }
        },
        desired_skills_canon: {
            name: "Habilidades deseables (normalizadas)",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Id" },
            conditions: { hidden: true }
        },

        // ── Content (Markdown) ───────────────────────────────────
        summary: {
            name: "Resumen",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "2-3 frases gancho (se muestra en las tarjetas de listado)"
        },
        description_md: {
            name: "Descripción",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "Descripción completa del puesto en Markdown"
        },
        requirements_md: {
            name: "Requisitos",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "Lo que necesita la empresa — Markdown"
        },
        responsibilities_md: {
            name: "Responsabilidades",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "Tareas del día a día — Markdown"
        },
        benefits_md: {
            name: "Beneficios",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "Prestaciones y beneficios — Markdown"
        },
        about_company_md: {
            name: "Sobre la Empresa",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "Texto opcional sobre la empresa para esta vacante (override del perfil)"
        },

        // ── Compensation ─────────────────────────────────────────
        salary_min: {
            name: "Salario Mínimo",
            type: "number",
            description: "En moneda original"
        },
        salary_max: {
            name: "Salario Máximo",
            type: "number",
            description: "En moneda original"
        },
        salary_currency: {
            name: "Moneda",
            type: "string",
            description: "ISO 4217: USD, EUR, MXN…"
        },
        salary_period: {
            name: "Periodo Salarial",
            type: "string",
            enum: [
                { id: "annual", label: "Anual", color: "blue" },
                { id: "monthly", label: "Mensual", color: "teal" },
                { id: "hourly", label: "Por hora", color: "orange" }
            ]
        },
        salary_display: {
            name: "Salario (Texto)",
            type: "string",
            description: "Formato legible: '$50,000 – $70,000 USD/año'"
        },
        // Converted from salary_min/max at the posting's currency. Set the
        // amounts in their real currency above; these exist only so vacancies
        // in different currencies can be sorted against each other.
        salary_usd_min: {
            name: "Salario USD Mínimo",
            type: "number",
            description: "Equivalente en USD para comparación",
            admin: { readOnly: true }
        },
        salary_usd_max: {
            name: "Salario USD Máximo",
            type: "number",
            description: "Equivalente en USD para comparación",
            admin: { readOnly: true }
        },
        compensation_notes: {
            name: "Notas de Compensación",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "Incluye equity, bono anual, etc."
        },
        show_salary: {
            name: "Mostrar salario",
            type: "boolean",
            description: "Mostrar el salario públicamente en la vacante",
            defaultValue: true
        },

        // ── Categorization ───────────────────────────────────────
        tags: {
            name: "Etiquetas",
            type: "array",
            columnType: "text[]",
            of: {
                type: "string",
                name: "Etiqueta",
                enum: [
                    { id: "esg", label: "ESG", color: "green" },
                    { id: "sostenibilidad", label: "Sostenibilidad", color: "teal" },
                    { id: "finanzas_sostenibles", label: "Finanzas Sostenibles", color: "blue" },
                    { id: "consultoria", label: "Consultoría", color: "purple" },
                    { id: "energia_renovable", label: "Energía Renovable", color: "yellow" },
                    { id: "economia_circular", label: "Economía Circular", color: "orange" },
                    { id: "cambio_climatico", label: "Cambio Climático", color: "red" },
                    { id: "reportes_esg", label: "Reportes ESG", color: "blue" },
                    { id: "cumplimiento_normativo", label: "Cumplimiento Normativo", color: "gray" },
                    { id: "biodiversidad", label: "Biodiversidad", color: "green" },
                    { id: "gestion_ambiental", label: "Gestión Ambiental", color: "teal" },
                    { id: "responsabilidad_social", label: "Responsabilidad Social", color: "purple" },
                    { id: "huella_carbono", label: "Huella de Carbono", color: "orange" },
                    { id: "gestion_residuos", label: "Gestión de Residuos", color: "yellow" },
                    { id: "recursos_hidricos", label: "Recursos Hídricos", color: "blue" },
                    { id: "gobernanza_corporativa", label: "Gobernanza Corporativa", color: "gray" },
                    { id: "cadena_suministro", label: "Cadena de Suministro Sostenible", color: "teal" },
                    { id: "impacto_social", label: "Impacto Social", color: "purple" },
                    { id: "desarrollo_sostenible", label: "Desarrollo Sostenible", color: "green" },
                    { id: "innovacion_verde", label: "Innovación Verde", color: "teal" }
                ]
            },
            description: "Etiquetas de dominio sostenibilidad y ESG"
        },
        skills: {
            name: "Habilidades",
            type: "array",
            columnType: "text[]",
            of: {
                type: "string",
                name: "Habilidad",
                enum: [
                    { id: "analisis_datos", label: "Análisis de Datos", color: "blue" },
                    { id: "reporting_esg", label: "Reporting ESG", color: "green" },
                    { id: "gri", label: "GRI", color: "teal" },
                    { id: "sasb", label: "SASB", color: "teal" },
                    { id: "tcfd", label: "TCFD", color: "teal" },
                    { id: "iso_14001", label: "ISO 14001", color: "purple" },
                    { id: "iso_45001", label: "ISO 45001", color: "purple" },
                    { id: "iso_50001", label: "ISO 50001", color: "purple" },
                    { id: "huella_carbono", label: "Cálculo Huella de Carbono", color: "orange" },
                    { id: "ciclo_vida", label: "Análisis de Ciclo de Vida", color: "orange" },
                    { id: "gestion_proyectos", label: "Gestión de Proyectos", color: "blue" },
                    { id: "stakeholders", label: "Gestión de Stakeholders", color: "gray" },
                    { id: "regulacion_ambiental", label: "Regulación Ambiental", color: "red" },
                    { id: "excel", label: "Excel", color: "green" },
                    { id: "python", label: "Python", color: "yellow" },
                    { id: "power_bi", label: "Power BI", color: "yellow" },
                    { id: "comunicacion", label: "Comunicación", color: "gray" },
                    { id: "ingles", label: "Inglés", color: "blue" },
                    { id: "economia_circular", label: "Economía Circular", color: "orange" },
                    { id: "auditoria", label: "Auditoría", color: "red" },
                    { id: "finanzas", label: "Finanzas", color: "blue" },
                    { id: "gestion_ambiental", label: "Gestión Ambiental", color: "teal" },
                    { id: "taxonomia_eu", label: "Taxonomía UE", color: "purple" },
                    { id: "csrd", label: "CSRD", color: "purple" },
                    { id: "due_diligence", label: "Due Diligence", color: "gray" }
                ]
            },
            description: "Competencias técnicas y profesionales requeridas"
        },
        industry: {
            name: "Industria",
            type: "string",
            description: "Sector: Energía, Consultoría, Finanzas…"
        },
        sustainability_focus: {
            name: "Enfoque de Sostenibilidad",
            type: "array",
            columnType: "text[]",
            of: {
                type: "string",
                name: "Enfoque",
                enum: [
                    { id: "estrategia_esg", label: "Estrategia ESG/Sostenibilidad", color: "blue" },
                    { id: "informes_sostenibilidad", label: "Informes Sostenibilidad", color: "purple" },
                    { id: "gestion_gobernanza", label: "Gestión gobernanza", color: "gray" },
                    { id: "compliance", label: "Compliance", color: "red" },
                    { id: "materialidad", label: "Materialidad", color: "teal" },
                    { id: "cambio_climatico", label: "Cambio climático", color: "red" },
                    { id: "energia", label: "Energía", color: "yellow" },
                    { id: "agua", label: "Agua", color: "blue" },
                    { id: "economia_circular", label: "Economía circular", color: "orange" },
                    { id: "biodiversidad", label: "Biodiversidad", color: "green" },
                    { id: "impacto_social", label: "Impacto social", color: "purple" },
                    { id: "dei", label: "Diversidad, equidad e inclusión", color: "pink" },
                    { id: "derechos_humanos", label: "Derechos Humanos", color: "indigo" },
                    { id: "finanzas_sostenibles", label: "Finanzas sostenibles", color: "green" },
                    { id: "inversion_impacto", label: "Inversión de impacto", color: "emerald" },
                    { id: "cadena_suministro_sostenible", label: "Cadena de suministro sostenible", color: "orange" },
                    { id: "otro", label: "OTRO", color: "gray" }
                ]
            },
            description: "Pilares ESG del puesto",
            // The areas, the level required in each and the text behind "OTRO"
            // are one answer, and the site has always asked for it with one
            // control: chips to pick the areas, a graded card per pick. Three
            // generic editors asked for it three times, and the middle one — a
            // bare key/value map with the area id typed by hand — let a level
            // name an area the vacancy had not picked.
            //
            // This field is that control, and it writes all three columns. The
            // other two are hidden from the form below; they keep their table
            // columns, since hiding is about the form and not about the data.
            //
            // Full width: the derived span for an array is half the grid, and
            // half a grid is not enough for 17 chips and a two-column run of
            // graded cards — they wrapped one word per line.
            admin: { span: 4, Field: "../fields/SustainabilityFocusField" }
        },
        sustainability_focus_levels: {
            name: "Niveles de Enfoque de Sostenibilidad",
            type: "map",
            keyValue: true,
            description: "Nivel de experiencia requerido para cada enfoque",
            conditions: { hidden: true }
        },
        sustainability_focus_other_text: {
            name: "Otro Enfoque de Sostenibilidad",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Otra especialidad" },
            description: "Textos para la opción OTRO del enfoque de sostenibilidad (múltiples entradas)",
            conditions: { hidden: true }
        },

        // ── Application ──────────────────────────────────────────
        application_url: {
            name: "URL donde se postulan",
            type: "string",
            description: "Enlace externo para aplicar"
        },
        application_email: {
            name: "Email al que se postulan",
            type: "string",
            description: "Email directo para aplicar (si no hay URL)"
        },
        // The single most consequential field on a vacancy, and the one that was
        // hardest to read. It decides whether an applicant lands in
        // `talent_applications` or leaves for someone else's site — 41 of 41
        // applications ever received came from the 4 rows marked `internal`, and
        // the other 103 rows are a directory we host but do not take part in.
        // The labels say that outcome instead of naming a mechanism.
        application_method: {
            name: "Dónde se postula",
            type: "string",
            defaultValue: "internal",
            // Green on the exception, grey on the rule. `external` is 98 of 107
            // rows, so colouring it orange painted almost the whole list a
            // warning colour and left nothing for the eye to catch — the 4 rows
            // that actually feed our candidate pipeline were the hardest to
            // find on a screen where everything shouted.
            enum: [
                { id: "internal", label: "En SustenTalent", color: "green" },
                { id: "external", label: "En un sitio externo", color: "gray" },
                { id: "email", label: "Por correo", color: "blue" }
            ],
            description:
                "Sólo 'En SustenTalent' genera una postulación en el panel. Las otras dos envían al candidato fuera y no dejan registro."
        },

        // ── Provenance ───────────────────────────────────────────
        // Two groups, not five options: a vacancy either came from a company
        // that filled the form on the site, or we imported it from somewhere
        // else. The old labels hid that — `submission` read "Postulación",
        // which is what a *candidate* does, and the three importers were named
        // after the vendor's API rather than after what they are.
        source: {
            name: "Origen",
            type: "string",
            // Same rule as `application_method` above: the common case is grey.
            // `submission` is 103 of 107 rows, so it is the background against
            // which "Importada" is worth noticing, not something to highlight.
            enum: [
                { id: "submission", label: "Publicada por la empresa", color: "gray" },
                { id: "manual", label: "Cargada por el equipo", color: "teal" },
                { id: "jsearch", label: "La encontramos en otra web · JSearch", color: "blue" },
                { id: "serpapi", label: "La encontramos en otra web · SerpAPI", color: "blue" },
                { id: "url_extract", label: "La importamos desde una URL", color: "blue" }
            ]
        },
        source_url: {
            name: "URL del anuncio original",
            type: "string"
        },
        external_id: {
            name: "Id que le puso la web de origen",
            type: "string",
            description: "Clave de deduplicación del scraper"
        },
        // Derived from `source`, and the only reason it is a column: it is what
        // `sort` below and the public board both order by, and an order can name
        // a column or nothing at all. See `deriveJobProvenance` at the top of
        // this file for the rule and drizzle/0058 for why it is stored.
        //
        // Not shown anywhere. `source` says the same thing with more precision
        // and is already in the form's rail and in two filter presets; a second
        // control repeating it in coarser terms would be a field to maintain, a
        // field to misread, and — being read-only — a field nobody can act on.
        is_imported: {
            name: "La trajimos de otra web",
            type: "boolean",
            admin: { readOnly: true, hideFromCollection: true }
        },
        // The two ends of a vacancy's life, and the pair the list shows. Short
        // names on purpose: these are column headers now, and "Fecha de
        // Publicación Original" wrapped to two lines in every row.
        posted_at: {
            name: "Publicada",
            type: "date",
            description:
                "Fecha en que la oferta se publicó en su origen. Coincide con la fecha de alta en las 103 vacantes que la traen; las 4 restantes la tienen vacía."
        },
        expires_at: {
            name: "Caduca (según el anuncio original)",
            type: "date",
            // Null on all 107 rows: the scraper's LLM extraction has never
            // filled it. `retire-stale-jobs` reads LEAST(expires_at,
            // closing_date), so today the deadline is `closing_date` alone —
            // which is why that one, not this, is the column in the list.
            description: "Extraída por el scraper. Hoy vacía en todas las vacantes."
        },
        closing_date: {
            name: "Cierra",
            type: "date",
            description: "El día que deja de aceptar candidaturas. Al llegar, el sistema la cierra solo."
        },
        application_instructions: {
            name: "Cómo hay que postularse",
            type: "string",
            admin: { multiline: true, markdown: true },
            description: "Instrucciones adicionales sobre cómo aplicar a la vacante"
        },

        // ── Meta ────────────────────────────────────────
        featured: {
            name: "Destacado",
            type: "boolean",
            description: "Fijado arriba del listado"
        },
        is_verified: {
            name: "Verificada",
            type: "boolean",
            description: "Publicada directamente por la empresa en SustenTalent"
        },
        // Meant for traffic counters and never incremented by anything: the
        // site's vacancy form writes 0 and that was the whole of it. The real
        // figures live in `job_traffic` (drizzle/0068), because an UPDATE of this
        // row fires `jobs_updated_at_touch` and the CDC trigger — a page view
        // would read as an edit. Hidden rather than dropped while the deployed
        // form still sends them; a column removed under it fails submissions.
        view_count: {
            name: "Vistas (sin uso)",
            type: "number",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        apply_count: {
            name: "Clics en «Postularme» (sin uso)",
            type: "number",
            admin: { readOnly: true },
            conditions: { hidden: true }
        },
        // When it last went live, stamped by the `jobs_published_at_stamp`
        // trigger on the transition into `published` (drizzle/0068) — not
        // `posted_at`, which is when the employer *sent* it, on average 8 days
        // before review let it out. What "lleva N días en el tablón" counts from.
        published_at: {
            name: "En el tablón desde",
            type: "date",
            description: "Cuándo se publicó por última vez. Lo pone la base de datos al publicarla; vacío en las anteriores al historial.",
            admin: { readOnly: true }
        },
        // Written by `remind-closing-vacancies` after the mail server accepted
        // the "cierra pronto" email to `poster_email`. See
        // services/closing-reminders.ts for why it is compared against the
        // deadline rather than tested for emptiness.
        closing_reminder_sent_at: {
            name: "Aviso de cierre enviado",
            type: "date",
            description: "Cuándo se avisó al publicador de que la vacante cerraba en una semana, con enlaces para renovarla o cerrarla.",
            admin: { readOnly: true }
        },
        llm_processed: {
            name: "Ya lo limpió la IA",
            type: "boolean",
            admin: { readOnly: true }
        },
        company_link_checked: {
            name: "Enlace de empresa verificado",
            type: "boolean",
            defaultValue: false,
            description: "El cron de enriquecimiento ya resolvió la empresa de esta vacante. Desmarcar para volver a intentarlo."
        },
        llm_model: {
            name: "Qué modelo de IA lo limpió",
            type: "string",
            admin: { readOnly: true }
        },

        // ── Company Submission Fields ────────────────────────────
        poster_email: {
            name: "Email del Publicador",
            type: "string",
            description: "Correo de contacto de quien publicó la vacante"
        },
        privacy_accepted: {
            name: "Privacidad Aceptada",
            type: "boolean",
            description: "Consentimiento legal del publicador"
        },
        auth_user_id: {
            name: "Publicado por",
            type: "string",
            userSelect: true,
            description: "Usuario que envió la vacante",
            admin: { readOnly: true }
        },

        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizado",
            type: "date",
            autoValue: "on_update"
        },

        // No relation tab for `talent_applications`. The "Postulaciones" tab
        // (views/ApplicantsPanel) shows those same rows against what the vacancy
        // asks for and can advance each application's status, and a second tab
        // listing them plainly was the same data twice. The panel queries the
        // collection by `job_id` directly, so it needs no relation declared
        // here; `talents` keeps its own, which is where a person's applications
        // belong.
        //
        // There used to be a second relation as well, listing candidates the
        // matching feature had paired with this job. It is gone: the only people
        // a vacancy names now are the ones who applied to it.
    },
    admin: {
        group: "Vacantes",
        icon: "Briefcase",
        /**
         * Full screen, not a side panel.
         *
         * On 2026-09-06 a vacancy opened in a strip about 180 px wide beside
         * the table — the title wrapped four times and the cover did not fit —
         * and nothing said the panel could be widened. A record's cover is a
         * page: "Lo siguiente", the profile, the correspondence. The queue
         * views keep their own side panel over the queue (see
         * `collectionViews` in frontend/src/App.tsx); this is the list.
         */
        openEntityMode: "full_screen",
        entityActions: [viewOnPublicBoard],
        /**
         * A vacancy opens on its cover, not on its form.
         *
         * `resumen` is registered in `frontend/src/App.tsx` and drawn by
         * `views/JobOverview.tsx`. Eighty properties in declaration order is
         * the screen for correcting a posting; what a reader wants is the dozen
         * facts that describe it and whether anybody is waiting on an answer.
         *
         * A builder rather than the bare key: a vacancy being created has no id,
         * so its candidaturas point at a row that does not exist and the cover
         * would be three empty sections. "Añadir" opens the form.
         */
        defaultSelectedView: ({ status }) => (status === "new" ? undefined : "resumen"),
        // Deliberately not "table". The table renderer comes up blank for this
        // collection — no header, no rows — and fires ~66 duplicate
        // `companies/count` requests per load, apparently looping on the
        // `company_id` relation column. The list view renders the same data
        // correctly and reads better for vacancies anyway. Revisit once the
        // rebase table bug is fixed.
        defaultViewMode: "list",
        // Ours first, then newest — the same order the public board runs, for the
        // same reason. The list opens on 55 live vacancies: 17 a company sent us
        // or the team loaded by hand, and 38 we brought in from other job
        // boards. Sorted by date alone the first 17 are scattered through the
        // other 38, because the importers post every weekday and an employer
        // submits one every few weeks — so the rows that carry a client are the
        // ones you have to hunt for. `is_imported` ASC is false-then-true: not
        // imported first. The two provenance presets below still split the list
        // outright when that is what you want.
        //
        // `created_at`, not `posted_at`, for the tiebreak: the latter is null on
        // 4 of 107 rows and Postgres sorts NULLS FIRST under DESC, so the four
        // vacancies with no publication date sat at the top of the table — the
        // opposite of "most recent first". `created_at` is set on every row.
        sort: [["is_imported", "asc"], ["created_at", "desc"]],
        // Open on the live board, not on the archive.
        //
        // `retire-stale-jobs` closes every vacancy past its deadline, and the
        // first run had 74 of 107 to catch up on — so without this the list
        // opens on a majority of dead listings and the working set is buried.
        // Nothing is hidden permanently: `defaultFilter` is a starting point the
        // user can change, the "Archivadas" preset below is one click away, and
        // `closed` is a status, not a deletion.
        //
        // `!=` rather than `in` over the live statuses on purpose: a status added
        // later stays visible under `!=` and would silently vanish under `in`.
        // The one thing `!=` also excludes is a NULL status — none exist today,
        // and a row with no status at all is an anomaly the "Archivadas" chip
        // will not surface either. Worth knowing before adding a nullable state.
        defaultFilter: { status: ["!=", "closed"] },
        // `tags` used to hold a resolver that collapsed `source` and
        // `application_method` into two plain chips. Both enums later became
        // their own columns, and then left the list entirely — provenance and
        // apply-destination are filters, not things to read on every row. Both
        // are still in the form and still have their filter presets below.
        //
        // `image` is the employer's logo, reached through the relation rather
        // than stored on the job. 92 of 107 vacancies resolve to one; the rest
        // fall back to the collection icon, which is what that fallback is for.
        // The relation is already in hand — the list asks for `include: ["*"]`
        // — so this reads from the row it was given and issues no request.
        display: {
            title: "title",
            subtitle: "company_name",
            status: "status",
            date: "created_at",
            // `company_id`, not a `company` key: an included relation resolves
            // *in place*, so the foreign-key field carries the target instead of
            // a uuid. It arrives in one of three shapes, and only the first is
            // obvious:
            //
            //   { __type, id, path, data: { id, path, values } }
            //        — what the admin holds. Two wrappers deep: a reference
            //          around an Entity around the row. `data` is what makes a
            //          relation chip navigable, `values` is the record.
            //   { id, name, logo_url, … }
            //        — the flat row, as the HTTP API returns it under
            //          `include=*`.
            //   "9ad2c53f-…"
            //        — a bare uuid, wherever nothing asked for the include.
            //
            // Reading `.logo_url` off either wrapper yields undefined without
            // erroring, which is why this presented as "the logo is missing from
            // the payload" rather than "the payload is two levels down".
            image: ({ entity }) => {
                const ref = (entity.values as any)?.company_id;
                if (!ref || typeof ref !== "object") return undefined;
                const company = ref.data?.values ?? ref.data ?? ref;
                return company?.logo_url || undefined;
            }
        },
        // The questions actually asked of 107 vacancies, in the order they get
        // asked. The first two are the same split seen from both sides, and
        // "Nuestras" is deliberately first: it is 4 rows, it is the entire
        // candidate pipeline, and it was previously indistinguishable from the
        // 103 listings we merely republish.
        //
        // The two `status` chips at the end are a pair, and they carry the one
        // job the others do not: making the default state *visible*. "Sin
        // archivar" matches `defaultFilter` exactly, so it renders already
        // active on load — otherwise the list opens showing 33 of 107 rows with
        // nothing on screen explaining where the rest went. Chips are keyed by
        // filter key, so these two replace each other rather than stacking, and
        // turning the active one off drops the `status` filter entirely and
        // shows everything. Live → archived → both, in one control.
        filterPresets: [
            {
                label: "Nuestras · postulan aquí",
                filterValues: { application_method: ["==", "internal"] }
            },
            {
                label: "Se postulan fuera",
                filterValues: { application_method: ["==", "external"] }
            },
            {
                label: "Las envió una empresa",
                filterValues: { source: ["==", "submission"] }
            },
            {
                label: "Las trajimos de otras webs",
                filterValues: {
                    source: ["in", ["jsearch", "serpapi", "url_extract"]]
                }
            },
            {
                label: "Abiertas y borradores",
                filterValues: { status: ["!=", "closed"] }
            },
            {
                label: "Cerradas",
                filterValues: { status: ["==", "closed"] }
            }
        ],
        inlineEditing: true,
        // Without this the table renders all 66 columns and is unreadable. These
        // six are what identifies a vacancy at a glance; everything else is
        // reachable by opening it. Deliberately excludes the salary_usd_* and
        // salary_display columns — they are empty in all 103 live rows.
        // `company_name` is omitted on purpose: the list view already renders it
        // as each row's subtitle (via `display.subtitle`), so listing it again
        // printed the company twice on every row.
        listProperties: [
            "title",
            "status",
            "location_display",
            // What the digest filters on, where a wrong one is easy to spot:
            // it sits beside the title it was read from.
            "job_function",
            // Both halves of what used to be one column: a job can be full time
            // *and* fixed term, and either can be unknown. See drizzle/0026.
            "employment_type",
            "contract_type",
            // The window the vacancy is open for, which is what the list was
            // missing: a published row says nothing about whether it is still
            // worth a candidate's time.
            //
            // `posted_at` and not `created_at`, even though `sort` is still
            // `created_at`: on all 103 rows that carry a publication date the
            // two fall on the same day, so the visible column stays monotonic
            // under that sort, and "Publicada" is the honest name for the fact.
            // The 4 rows without one render blank rather than borrowing our
            // ingest date. `created_at` stays reachable in the form.
            //
            // `closing_date` and not `expires_at`: the deadline the retire cron
            // acts on is LEAST() of the two, and `expires_at` is null on every
            // row — so this column *is* the deadline. 89 of 107 rows have it.
            "posted_at",
            "closing_date"
        ],
        // Flat fields only. A preview that includes `company_id` renders a
        // relation card *inside* the relation card that is previewing this job —
        // an interactive selector nested in another selector. `company_name`
        // says the same thing and is plain text.
        previewProperties: ["title", "company_name", "status"],
        // 66 fields in one flat run is not a form, it is a data dump. The
        // comment-headed blocks in `propertiesOrder` below already described the
        // right grouping — this turns them into real collapsible sections, and
        // moves status/flags/provenance out of the reading column into the rail.
        //
        // Everything a human writes is open by default; everything the pipeline
        // writes starts collapsed. A validation error inside a collapsed section
        // expands it, so nothing can hide.
        form: {
            sidebar: [
                "status",
                "return_note",
                "returned_at",
                "featured",
                "is_verified",
                "company_id",
                "posted_at",
                "published_at",
                "closing_date",
                "closing_reminder_sent_at",
                "source",
                "auth_user_id"
            ],
            sections: [
                {
                    key: "identity",
                    properties: ["title", "company_name", "summary", "slug"]
                },
                {
                    key: "content",
                    title: "Contenido",
                    properties: [
                        "description_md",
                        "requirements_md",
                        "responsibilities_md",
                        "benefits_md",
                        "about_company_md"
                    ]
                },
                {
                    key: "role",
                    title: "Detalles del puesto",
                    properties: [
                        // Both halves of the drizzle/0026 split. `contract_type`
                        // was added to `listProperties` and to `properties` but
                        // to no section, so the form never rendered it: the only
                        // way to record that a vacancy is fixed-term was to put
                        // it back in `employment_type`, which the form then
                        // refuses to save.
                        "job_function",
                        "employment_type",
                        "contract_type",
                        "seniority",
                        "department",
                        "experience_years",
                        "required_studies",
                        "required_languages",
                        "travel_required"
                    ]
                },
                {
                    // Its own section rather than more fields under "Detalles
                    // del puesto": these four are the ones matching reads, and
                    // burying them under a heading about contract type and
                    // seniority is how `required_studies` ended up filled in on
                    // 4 vacancies out of 114.
                    key: "credentials",
                    title: "Certificaciones y habilidades",
                    properties: [
                        "required_certifications",
                        "desired_certifications",
                        "required_skills",
                        "desired_skills"
                    ]
                },
                {
                    key: "focus",
                    title: "Áreas y categorización",
                    properties: [
                        "sustainability_focus",
                        "sustainability_focus_levels",
                        "sustainability_focus_other_text",
                        "tags",
                        "skills",
                        "industry"
                    ]
                },
                {
                    key: "location",
                    title: "Ubicación y modalidad",
                    properties: [
                        "work_model",
                        "remote_regions",
                        "location_display",
                        "location_city",
                        "location_state",
                        "location_country",
                        "location_country_code",
                        "location_continent",
                        "location_lat",
                        "location_lng"
                    ]
                },
                {
                    key: "salary",
                    title: "Compensación",
                    properties: [
                        "show_salary",
                        "salary_min",
                        "salary_max",
                        "salary_currency",
                        "salary_period",
                        "salary_display",
                        "salary_usd_min",
                        "salary_usd_max",
                        "compensation_notes"
                    ]
                },
                {
                    key: "apply",
                    title: "Cómo postular",
                    properties: [
                        "application_method",
                        "application_url",
                        "application_email",
                        "application_instructions"
                    ]
                },
                {
                    key: "pipeline",
                    title: "Origen y automatización",
                    // Machine-written provenance and counters. Read it when you
                    // are debugging the pipeline; never while editing a vacancy.
                    collapsed: true,
                    properties: [
                        "source_url",
                        "external_id",
                        "expires_at",
                        "llm_processed",
                        "llm_model",
                        "company_link_checked",
                        "poster_email",
                        "privacy_accepted"
                    ]
                }
            ]
        },
        propertiesOrder: [
            // Identity & Status
            "title",
            "status",
            "return_note",
            "returned_at",
            "company_id",
            "company_name",
            "slug",
            "featured",
            "is_verified",

            // Content (Markdown)
            "summary",
            "description_md",
            "requirements_md",
            "responsibilities_md",
            "benefits_md",
            "about_company_md",

            // Employment details
            "job_function",
            "employment_type",
            "seniority",
            "department",
            "experience_years",
            "required_studies",
            "required_languages",
            "travel_required",

            // Certifications and skills. The `_canon` siblings are omitted:
            // they are hidden from the form, and listing them here would only
            // fix the order of fields nobody sees.
            "required_certifications",
            "desired_certifications",
            "required_skills",
            "desired_skills",

            // Categorization
            "tags",
            "skills",
            "industry",
            "sustainability_focus",
            "sustainability_focus_levels",
            "sustainability_focus_other_text",

            // Application
            "application_url",
            "application_email",
            "application_method",
            "application_instructions",

            // Work model & Location
            "work_model",
            "remote_regions",
            "location_display",
            "location_city",
            "location_state",
            "location_country",
            "location_country_code",
            "location_continent",
            "location_lat",
            "location_lng",

            // Compensation
            "salary_display",
            "salary_min",
            "salary_max",
            "salary_currency",
            "salary_period",
            "salary_usd_min",
            "salary_usd_max",
            "compensation_notes",
            "show_salary",

            // Provenance
            "source",
            "source_url",
            "external_id",
            "posted_at",
            "published_at",
            "expires_at",
            "closing_date",
            "closing_reminder_sent_at",

            // Metrics & System
            "view_count",
            "apply_count",
            "llm_processed",
            "llm_model",
            "poster_email",
            "privacy_accepted",
            "auth_user_id",
            "created_at",
            "updated_at"
        ]
    }
};

export default jobsCollection;
